import Backdrop from '@/components/ui/backdrop';
import { CustomerUser } from '@/core/entities';
import { useRoles, useUsersBySite } from '@/queries/users';
import AdminPanelPage from '@/views/layouts/admin-panel/AdminPanelPage';
import { Box, Paper, Stack } from '@mui/material';
import { FunctionComponent, ReactElement, useEffect, useMemo, useState } from 'react';
import useAuth from '@/shared/context/auth-context';
import { Roles } from '@/core/types';
import DataGrid, { Column, FilterRow, HeaderFilter, Paging, SearchPanel, Sorting } from 'devextreme-react/data-grid';

const CustomerUsersPage: FunctionComponent = (): ReactElement => {
    const { user } = useAuth();

    const { data: roles } = useRoles();
    const roleOptions = useMemo(
        () => roles?.filter((role) => role.title !== Roles.LEVIANT_ADMIN),
        [roles]
    );

    const [users, setUsers] = useState<CustomerUser[]>([]);

    const siteId = useMemo(() => user?.sites[0]?.id, [user?.sites]);

    const { query: useUsersQuery } = useUsersBySite(siteId, {
        limit: 99999,
    });

    const { data: userPages, isLoading } = useUsersQuery;

    useEffect(() => {
        if (userPages) {
            setUsers(userPages.pages[0].data);
        }
    }, [userPages]);

    const getRoleTitle = (roleId: string): string => {
        const role = roleOptions?.find((r) => r.id === roleId);
        return role?.title || '-';
    };

    const roleFilterOptions = useMemo(
        () => roleOptions?.map((role) => ({ text: role.title, value: role.title })) || [],
        [roleOptions]
    );

    if (!roleOptions) return <Backdrop />;

    return (
        <AdminPanelPage title="Users">
            {isLoading ? (
                <Backdrop />
            ) : (
                <Box component={Paper} p={3}>
                    <Stack direction="column" spacing={3}>
                        <DataGrid
                            dataSource={users}
                            showBorders={true}
                            columnAutoWidth={true}
                            rowAlternationEnabled={true}
                            allowColumnResizing={true}
                            allowColumnReordering={true}
                        >
                            <Sorting mode="multiple" />
                            <FilterRow visible={true} />
                            <HeaderFilter visible={true} />
                            <SearchPanel visible={true} highlightCaseSensitive={true} />
                            <Paging defaultPageSize={50} enabled={true} />

                            <Column dataField="firstName" caption="First Name" />
                            <Column dataField="lastName" caption="Last Name" />
                            <Column dataField="email" caption="Email" />
                            <Column dataField="pin" caption="Pin" alignment="right" />
                            <Column
                                caption="Role"
                                dataField="roleId"
                                calculateCellValue={(rowData: CustomerUser) => getRoleTitle(rowData.roleId)}
                                allowSorting={true}
                                allowFiltering={true}
                            >
                                <HeaderFilter dataSource={roleFilterOptions} />
                            </Column>
                            <Column
                                caption="Sites"
                                calculateCellValue={(rowData: CustomerUser) =>
                                    rowData.sites?.map((site) => site.name).join(', ') || '-'
                                }
                                allowFiltering={false}
                            />
                        </DataGrid>
                    </Stack>
                </Box>
            )}
        </AdminPanelPage>
    );
};

export default CustomerUsersPage;
